"use client"

import * as React from "react"
import { Navigate, useNavigate, useSearchParams } from "react-router-dom"

import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import { usePendingIncomingTransfersCount } from "@/features/finance/hooks/use-pending-incoming-transfers-count"
import { SavedCalculationsPanel } from "@/features/finance/containers/transfers/saved-calculations-panel"
import { TransferRowActions } from "@/features/finance/containers/transfers/transfer-row-actions"
import type { ColumnDef } from "@tanstack/react-table"
import { ArrowLeftRightIcon, PlusIcon } from "lucide-react"

import { useActiveBranchSlug, useActiveWorkspaceSlug, useShowMultiBranchWorkspaceUi } from "@/features/auth"
import { useCreateOverlay } from "@/features/create-overlay/hooks/use-create-overlay"
import { CreateKind, CreatePrefillKey } from "@/features/create-overlay/model/create-kind"
import { useTranslations } from "@/i18n"
import { AliveLeadCell, AliveMark, AlivePillTone, AliveStatusCell, DataTableVariant } from "@/shared/components/alive-cells"
import { ExportAction, exportFileName } from "@/shared/components/export"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { DataTable } from "@ibada/ui/components/data-table"
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from "@ibada/ui"
import { FinanceTransferStatus } from "@/features/finance/model/finance-types"
import type { FinanceTransferRequestRecord } from "@/features/finance/model/finance-types"
import { useFinanceTransferRequestsQuery } from "@/features/finance/services/finance-query"
import { formatMoney } from "@/features/finance/lib/finance-utils"
import { getBranchDisplayLabel } from "@/shared/lib/branch/branch-display"
import { dataTableTransactionsActionsColumnSizing } from "@/shared/lib/data-table/data-table-overflow-actions-column"
import { dbDateToDateOnly } from "@ibada/shared-types"

enum TransferViewTab {
  /**
   * Everything that concerns this branch, sent or received.
   *
   * The default, and deliberately so: a branch that has sent four remittances and received none opened this
   * page on the incoming tab and saw an empty table, which reads as "no transfers" rather than "none for you
   * to decide on".
   */
  All = "all",
  Awaiting = "awaiting",
  Incoming = "incoming",
  Outgoing = "outgoing",
  Rejected = "rejected",
}

type TransferTableRow = {
  id: string
  reference: string
  amount: string
  date: string
  fund: string
  financialAccount: string
  /** The OTHER side — who it is going to, or coming from, depending on the tab. */
  counterparty: string
  status: FinanceTransferStatus
  /** Who did the last thing to it, and what. The full chain is on the tooltip. */
  activity: TransferActivity
  /**
   * The untouched record, for the actions menu.
   *
   * The menu needs both branch ids, the reversal timestamp and the status to decide what to offer, and
   * re-flattening those into display strings would only mean parsing them back.
   */
  record: FinanceTransferRequestRecord
}

/**
 * The lifecycle as a list of who-did-what, newest last.
 *
 * A transfer is a two-sided agreement, so one "created by" column cannot answer the question people actually
 * ask — "who approved this?". Rendering all five actors as columns would give four empty ones on every row,
 * so the table shows the LATEST step and keeps the rest a hover away.
 */
/** Pending is the one awaiting a person; approved is done; a refusal or withdrawal is terminal and negative. */
const STATUS_TONE: Partial<Record<FinanceTransferStatus, AlivePillTone>> = {
  [FinanceTransferStatus.Draft]: AlivePillTone.Neutral,
  // Pending is work waiting on someone, so it needs the same warning vocabulary as other attention queues.
  [FinanceTransferStatus.Pending]: AlivePillTone.Warning,
  [FinanceTransferStatus.Approved]: AlivePillTone.Success,
  [FinanceTransferStatus.Rejected]: AlivePillTone.Danger,
  [FinanceTransferStatus.Cancelled]: AlivePillTone.Neutral,
}

type TransferActivity = {
  latest: { label: string; actor: string; at: string } | null
  trail: Array<{ label: string; actor: string; at: string }>
}

/**
 * The who-did-what trail, in the order it happened.
 *
 * Built from the timestamps rather than the status, so a transfer that was created, submitted and then
 * rejected shows all three steps — the status alone says only where it ended up, and "who approved this?"
 * cannot be answered from an endpoint state.
 */
function buildActivity(
  item: FinanceTransferRequestRecord,
  t: (key: string, values?: Record<string, string | number>) => string,
): TransferActivity {
  const name = (user: { firstName?: string | null; lastName?: string | null; email?: string | null } | null) =>
    user ? [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || (user.email ?? "—") : "—"

  const steps: Array<{ label: string; actor: string; at: string } | null> = [
    item.createdAt ? { label: t("activity.created"), actor: name(item.createdByUser), at: item.createdAt } : null,
    item.submittedAt
      ? { label: t("activity.submitted"), actor: name(item.submittedByUser ?? item.createdByUser), at: item.submittedAt }
      : null,
    item.approvedAt ? { label: t("activity.approved"), actor: name(item.approvedByUser), at: item.approvedAt } : null,
    item.rejectedAt ? { label: t("activity.rejected"), actor: name(item.rejectedByUser), at: item.rejectedAt } : null,
    item.cancelledAt ? { label: t("activity.cancelled"), actor: name(item.cancelledByUser), at: item.cancelledAt } : null,
  ]

  const trail = steps
    .filter((step): step is { label: string; actor: string; at: string } => step !== null)
    .sort((a, b) => a.at.localeCompare(b.at))

  return { latest: trail.at(-1) ?? null, trail }
}

function parseTransferViewTab(value: string | null): TransferViewTab {
  switch (value) {
    case TransferViewTab.Awaiting:
      return TransferViewTab.Awaiting
    case TransferViewTab.Outgoing:
      return TransferViewTab.Outgoing
    case TransferViewTab.Rejected:
      return TransferViewTab.Rejected
    case TransferViewTab.Incoming:
      return TransferViewTab.Incoming
    default:
      return TransferViewTab.All
  }
}

/**
 * Finance → Fund Transfers placeholder.
 *
 * Fund transfers are intentionally separated from account-to-account transfers because future branch-to-branch fund
 * movement is an operational workflow, not an immediate accounting operation. Internal account transfers stay under
 * Create → Fund Transfer.
 */
export function TransferRequestsPageContent() {
  const t = useTranslations("finance.transferRequests")
  const tCommon = useTranslations("common")
  const navigate = useNavigate()
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug()
  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const { activeBranch, canEditFinance } = useFinancePageState()
  const branchId = activeBranch?.id ?? null
  // The parish views this screen through its own finance permission, exactly as it does every other finance
  // page — and it is the side that APPROVES, so getting this wrong leaves approvals unreachable.
  const showMultiBranchWorkspaceUi = useShowMultiBranchWorkspaceUi()
  const [searchParams, setSearchParams] = useSearchParams()
  const pendingIncomingTransfersCount = usePendingIncomingTransfersCount(workspaceId, branchId)
  const activeTab = React.useMemo(() => parseTransferViewTab(searchParams.get("view")), [searchParams])
  React.useEffect(() => {
    if (searchParams.has("view") || pendingIncomingTransfersCount <= 0) return
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set("view", TransferViewTab.Awaiting)
        return params
      },
      { replace: true },
    )
  }, [pendingIncomingTransfersCount, searchParams, setSearchParams])

  // Creating opens over this list (the create overlay, `?create=transfer-request`).
  const { open: openCreateOverlay } = useCreateOverlay()
  const goToBranchTransferCreate = React.useCallback(() => openCreateOverlay(CreateKind.TransferRequest), [openCreateOverlay])

  const goToTransferEdit = React.useCallback(
    (transfer: FinanceTransferRequestRecord) =>
      navigate(
        buildBranchAdminUrl(branchSlug, `/finance/transfers/requests/${transfer.id}/edit`, workspaceSlug),
      ),
    [branchSlug, navigate, workspaceSlug],
  )

  const setActiveTab = React.useCallback(
    (next: TransferViewTab) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next === TransferViewTab.All && pendingIncomingTransfersCount <= 0) {
            params.delete("view")
          } else {
            params.set("view", next)
          }
          return params
        },
        { replace: true },
      )
    },
    [pendingIncomingTransfersCount, setSearchParams],
  )

  const columns = React.useMemo<ColumnDef<TransferTableRow>[]>(
    () => [
      {
        // The other side leads (who it goes to, or comes from), with the reference and date under it.
        accessorKey: "counterparty",
        id: "lead",
        header: t("table.columns.counterparty"),
        size: 280,
        cell: ({ row }) => (
          <AliveLeadCell
            leading={<AliveMark icon={ArrowLeftRightIcon} />}
            title={row.original.counterparty}
            subtitle={`${row.original.reference} · ${row.original.date}`}
          />
        ),
      },
      {
        accessorKey: "reference",
        header: t("table.columns.reference"),
        // Under the counterparty; still in the column picker.
        meta: { defaultHidden: true },
      },
      {
        accessorKey: "status",
        header: t("table.columns.status"),
        // SECOND, not last. Status is the first thing anyone wants from this table — whether a transfer needs
        // them — and at the far right it sat behind six columns and, on narrow screens, a horizontal scroll.
        // A badge, tone-coded: pending is the one waiting on a person, refusals are terminal.
        cell: ({ row }) => (
          <AliveStatusCell tone={STATUS_TONE[row.original.status] ?? AlivePillTone.Neutral}>
            {/* The status keys are the enum values verbatim (DRAFT, PENDING, ...): lowercasing missed them all. */}
            {t(`status.${row.original.status}` as never)}
          </AliveStatusCell>
        ),
      },
      {
        accessorKey: "amount",
        header: t("table.columns.amount"),
        meta: { align: "right" },
        cell: ({ row }) => <span className='text-[13px] font-extrabold tabular-nums'>{row.original.amount}</span>,
      },
      {
        accessorKey: "date",
        header: t("table.columns.date"),
        meta: { defaultHidden: true },
      },
      {
        accessorKey: "fund",
        header: t("table.columns.fund"),
      },
      {
        accessorKey: "financialAccount",
        header: t("table.columns.financialAccount"),
      },

      {
        id: "activity",
        header: t("table.columns.activity"),
        // ONE column, not five. Four of five actor columns are empty on any given row, and the question is
        // always "what happened last, and who did it" — the rest is a hover away.
        cell: ({ row }) => {
          const { latest, trail } = row.original.activity
          if (!latest) return <span className='text-muted-foreground'>-</span>
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='cursor-default'>
                  <span className='font-medium'>{latest.label}</span>{" "}
                  <span className='text-muted-foreground'>{latest.actor}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <span className='flex flex-col gap-0.5'>
                  {trail.map((step, index) => (
                    <span key={`${step.label}-${index}`} className='tabular-nums'>
                      {step.label} · {step.actor} · {step.at.slice(0, 10)}
                    </span>
                  ))}
                </span>
              </TooltipContent>
            </Tooltip>
          )
        },
      },
      {
        id: "actions",
        header: t("table.columns.actions"),
        enableHiding: false,
        // The shared narrow sizing (76px). Without it the column took DataTable's ~160px default — twice the
        // width an icon-only ⋯ trigger needs, and doubly wasteful now that it is pinned and that width is
        // taken out of the scrollable area on every screen.
        ...dataTableTransactionsActionsColumnSizing,
        // PINNED right, like every other actions column in the app. This table is wide — nine columns, one of
        // them a branch name — so on anything narrower than a desktop the menu scrolled off the edge and the
        // row's actions were only reachable by scrolling sideways first.
        meta: { align: "right", defaultPin: "right", label: t("table.columns.actions") },
        // This was an `accessorKey: "actions"` with no cell and no such field on the row — so it rendered an
        // empty cell on every row and the whole lifecycle (approve, reject, withdraw) was unreachable from
        // the table. The menu decides for itself what to offer, per row and per side.
        cell: ({ row }) => (
          <TransferRowActions
            transfer={row.original.record}
            workspaceId={workspaceId}
            branchId={branchId}
            canEdit={canEditFinance}
            onEdit={goToTransferEdit}
          />
        ),
      },
    ],
    [branchId, canEditFinance, goToTransferEdit, t, workspaceId],
  )

  /**
   * The list, per tab. Incoming = what this branch must decide on; Outgoing = what it sent; Rejected = the
   * refusals, from either side, which is why it filters on status rather than direction.
   */
  const listQuery = React.useMemo(() => {
    if (!workspaceId || !branchId) return null
    if (activeTab === TransferViewTab.All) return { workspaceId, branchId }
    if (activeTab === TransferViewTab.Awaiting) {
      return { workspaceId, destinationBranchId: branchId, status: FinanceTransferStatus.Pending }
    }
    if (activeTab === TransferViewTab.Incoming) return { workspaceId, destinationBranchId: branchId }
    if (activeTab === TransferViewTab.Outgoing) return { workspaceId, sourceBranchId: branchId }
    return { workspaceId, branchId, status: FinanceTransferStatus.Rejected }
  }, [activeTab, branchId, workspaceId])

  const transfersQuery = useFinanceTransferRequestsQuery(listQuery)

  const rows = React.useMemo<TransferTableRow[]>(
    () =>
      [...(transfersQuery.data?.items ?? [])]
        .sort((left, right) => {
          const leftPending = left.status === FinanceTransferStatus.Pending
          const rightPending = right.status === FinanceTransferStatus.Pending
          if (leftPending === rightPending) return 0
          return leftPending ? -1 : 1
        })
        .map((item) => {
        // On the All tab a row's direction is a property of the ROW, not of the tab — so the counterparty
        // column names the other side whichever way the money went.
        const incoming =
          activeTab === TransferViewTab.All
            ? item.destinationBranch?.id === branchId
            : activeTab === TransferViewTab.Incoming
        const other = incoming ? item.sourceBranch : item.destinationBranch
        return {
          id: item.id,
          // A transfer has no reference until it posts, so an unsent one shows a dash rather than a blank cell.
          reference: item.transferReference ?? item.reference ?? "—",
          amount: formatMoney(item.sourceAmount, item.currency),
          date: dbDateToDateOnly(item.occurredAt) ?? "—",
          fund: (incoming ? item.destinationFund : item.sourceFund)
            ? ((incoming ? item.destinationFund : item.sourceFund)!).name
            : "—",
          financialAccount: (incoming ? item.destinationAccount?.name : item.sourceAccount?.name) ?? "—",
          counterparty: other
            ? getBranchDisplayLabel({
                branchType: other.branchType,
                branchName: other.name,
                parishLabel: t("parish"),
                fallbackLabel: other.name,
              })
            : "—",
          status: item.status,
          activity: buildActivity(item, t),
          record: item,
        }
      }),
    [activeTab, branchId, t, transfersQuery.data?.items],
  )

  if (!showMultiBranchWorkspaceUi) {
    return <Navigate to={buildBranchAdminUrl(branchSlug, "/finance", workspaceSlug)} replace />
  }

  return (
    <section className='space-y-4'>
      {/* The recurring routine, one click away — see SavedCalculationsPanel. Renders nothing when empty. */}
      <SavedCalculationsPanel
        workspaceId={workspaceId}
        branchId={branchId}
        canEdit={canEditFinance}
        onApply={(calculation) =>
          openCreateOverlay(CreateKind.TransferRequest, { [CreatePrefillKey.SavedTransferCalculation]: calculation.id })
        }
      />

      <DataTable
        data={rows}
        columns={columns}
        variant={DataTableVariant.Alive}
        // A real route, so a transfer can be linked to — which is what the submitted/approved/rejected
        // notifications need somewhere specific to land on.
        onRowClick={(row) =>
          navigate(buildBranchAdminUrl(branchSlug, `/finance/transfers/requests/${row.id}`, workspaceSlug))
        }
        isLoading={transfersQuery.isPending}
        emptyMessage={t(`empty.${activeTab}`)}
        initialPagination={{ pageIndex: 0, pageSize: 20 }}
        toolbar={{
          // Phone: the toolbar draws the screen's one header (title, back, + and ⋯), pinned with the tabs.
          mobileHeader: { title: t("title") },
          views: {
            value: activeTab,
            onValueChange: (value) => setActiveTab(parseTransferViewTab(value)),
            items: [
              { value: TransferViewTab.All, label: t("views.all") },
              {
                value: TransferViewTab.Awaiting,
                label: (
                  <span className='inline-flex items-center gap-1.5'>
                    <span>{t("views.awaiting")}</span>
                    {pendingIncomingTransfersCount > 0 ? (
                      <Badge variant='warning' className='h-4 min-w-4 px-1 text-[10px] leading-none'>
                        {pendingIncomingTransfersCount}
                      </Badge>
                    ) : null}
                  </span>
                ),
              },
              { value: TransferViewTab.Incoming, label: t("views.incoming") },
              { value: TransferViewTab.Outgoing, label: t("views.outgoing") },
              { value: TransferViewTab.Rejected, label: t("views.rejected") },
            ],
          },
          display: {
            label: t("toolbar.display"),
            activeColumnsLabel: t("toolbar.activeColumns"),
            availableColumnsLabel: t("toolbar.availableColumns"),
            emptyColumnsLabel: t("toolbar.emptyColumns"),
            resetLabel: t("toolbar.resetColumns"),
          },
          secondaryActions: [
            {
              id: "export",
              // Phone: a round download icon in the title row (it opens the formats in a sheet).
              mobileIconOnly: true,
              label: tCommon("export.button"),
              emphasis: "priority" as const,
              order: 5,
              render: ({ inOverflow }: { inOverflow: boolean }) => (
                <ExportAction
                  renderAs={inOverflow ? "menuItem" : "button"}
                  itemCount={rows.length}
                  config={{
                    resource: "fundTransfers",
                    fileName: exportFileName(t("title")),
                    query: { workspaceId: workspaceId ?? undefined, branchId: branchId ?? undefined },
                  }}
                />
              ),
            },
          ],
          primaryAction: {
            id: "start-transfer",
            label: t("actions.start"),
            icon: <PlusIcon className='size-4' />,
            emphasis: "primary",
            onSelect: goToBranchTransferCreate,
          },
        }}
      />

    </section>
  )
}
